/**
 * Copyright (c) 2010-2020 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import {
    Directive,
    DoCheck,
    ElementRef,
    EventEmitter,
    Input,
    NgZone,
    OnDestroy,
    OnInit,
    Output,
    Renderer2,
} from '@angular/core';
import { interval, merge, Observable, Subject } from 'rxjs';
import { filter, map, mergeMap, pairwise, share, take, takeUntil, throttle } from 'rxjs/operators';
import { LoggerService } from '../../../modules/shared/services/logger.service';
import { BoundingRectangle, ClientCoordinate, Coordinate, Edges, ResizeEvent } from './interfaces';
import { PointerEventListeners } from './pointer-event-listeners';
import { ResizeObject } from './interfaces/resize-object';

export interface ResizeCursors {
    topLeft: string;
    topRight: string;
    bottomLeft: string;
    bottomRight: string;
    leftOrRight: string;
    topOrBottom: string;
}

const DEFAULT_RESIZE_CURSORS: ResizeCursors = Object.freeze({
    topLeft: 'nw-resize',
    topRight: 'ne-resize',
    bottomLeft: 'sw-resize',
    bottomRight: 'se-resize',
    leftOrRight: 'ew-resize',
    topOrBottom: 'ns-resize',
});

const RESIZE_ACTIVE_CLASS = 'resize-active';
const RESIZE_LEFT_HOVER_CLASS = 'resize-left-hover';
const RESIZE_RIGHT_HOVER_CLASS = 'resize-right-hover';
const RESIZE_TOP_HOVER_CLASS = 'resize-top-hover';
const RESIZE_BOTTOM_HOVER_CLASS = 'resize-bottom-hover';
const RESIZE_GHOST_ELEMENT_CLASS = 'resize-ghost-element';

export const MOUSE_MOVE_THROTTLE_MS = 50;

/**
 * Place this on an element to make it resizable. For example:
 *
 * ```html
 * <div
 *   mwlResizable
 *   [resizeEdges]="{bottom: true, right: true, top: true, left: true}"
 *   [enableGhostResize]="true">
 * </div>
 * ```
 */
@Directive({ selector: '[dashGisNg2Resizable]' })
export class DashGisNg2ResizableDirective implements OnInit, OnDestroy, DoCheck {
    /**
     * Flag to allow complete disabling of resizing
     */
    @Input() enableResize = true;

    /**
     * A function that will be called before each resize event. Return `true`
     * to allow the resize event to propagate or `false` to cancel it
     */
    @Input() validateResize: (resizeEvent: ResizeEvent) => boolean;

    /**
     * The edges that an element can be resized from. Pass an object like `{top: true, bottom: false}`.
     * By default no edges can be resized.
     */
    @Input() resizeEdges: Edges = {};

    /**
     * Set to `true` to enable a temporary resizing effect of the element in between the
     * `resizeStart` and `resizeEnd` events.
     */
    @Input() enableGhostResize = false;

    /**
     * A snap grid that resize events will be locked to.
     *
     * e.g. to only allow the element to be resized every 10px set it to `{left: 10, right: 10}`
     */
    @Input() resizeSnapGrid: Edges = {};

    /**
     * The mouse cursors that will be set on the resize edges
     */
    @Input() resizeCursors: ResizeCursors = DEFAULT_RESIZE_CURSORS;

    /**
     * Mouse over thickness to active cursor.
     */
    @Input() resizeCursorPrecision = 3;

    /**
     * Define the positioning of the ghost element (can be fixed or absolute)
     */
    @Input() ghostElementPositioning: 'fixed' | 'absolute' = 'fixed';

    /**
     * Called when the mouse is pressed and a resize event is about to begin. `$event` is a `ResizeEvent` object.
     */
    @Output() resizeStart = new EventEmitter<ResizeEvent>();

    /**
     * Called as the mouse is dragged after a resize event has begun. `$event` is a `ResizeEvent` object.
     */
    @Output() resizing = new EventEmitter<ResizeEvent>();

    /**
     * Called after the mouse is released after a resize event. `$event` is a `ResizeEvent` object.
     */
    @Output() resizeEnd = new EventEmitter<ResizeEvent>();

    /**
     * @hidden
     */
    public mouseup = new Subject<{
        clientX: number;
        clientY: number;
        edges?: Edges;
        event: MouseEvent | TouchEvent;
    }>();

    /**
     * @hidden
     */
    public mousedown = new Subject<{
        clientX: number;
        clientY: number;
        edges?: Edges;
        event: MouseEvent | TouchEvent;
    }>();

    /**
     * @hidden
     */
    public mousemove = new Subject<{
        clientX: number;
        clientY: number;
        edges?: Edges;
        event: MouseEvent | TouchEvent;
    }>();

    private listenerSubscriptions: any = {};
    private resizableListeners: PointerEventListeners;

    /**
     * @hidden
     */
    constructor(
        private renderer: Renderer2,
        private containerElement: ElementRef,
        private ngZone: NgZone,
        private logger: LoggerService
    ) {
        this.resizableListeners = PointerEventListeners.getInstance(renderer, ngZone);
        this.resizableListeners.initialize(containerElement);
    }

    /**
     * @hidden
     */
    ngOnInit(): void {
        if (this.enableResize) {
            // TODO - use some fancy Observable.merge's for this
            this.listenerSubscriptions.pointerDown$ = this.resizableListeners.pointerDown$.subscribe(
                ({ clientX, clientY, event }) => {
                    this.mousedown.next({ clientX, clientY, event });
                },
                (err) => this.logger.error(err)
            );

            this.listenerSubscriptions.pointerMove$ = this.resizableListeners.pointerMove$.subscribe(
                ({ clientX, clientY, event }) => {
                    this.mousemove.next({ clientX, clientY, event });
                }
            );

            this.listenerSubscriptions.pointerUp$ = this.resizableListeners.pointerUp$.subscribe(
                ({ clientX, clientY, event }) => {
                    this.mouseup.next({ clientX, clientY, event });
                }
            );

            let currentResize: ResizeObject | null;

            const removeGhostElement = () => {
                if (currentResize && currentResize.clonedNode) {
                    this.containerElement.nativeElement.parentElement.removeChild(currentResize.clonedNode);
                    this.renderer.removeClass(this.containerElement.nativeElement, 'resizing-hidden');
                }
            };

            const mouseMove: Observable<any> = this.mousemove.pipe(
                throttle(() => interval(MOUSE_MOVE_THROTTLE_MS)),
                // filter(() => !!currentResize),
                share()
            );

            mouseMove.subscribe(({ clientX, clientY }) => {
                const resizeEdges: Edges = this.getResizeEdges(
                    clientX,
                    clientY,
                    this.containerElement,
                    this.resizeEdges,
                    this.resizeCursorPrecision
                );
                const resizeCursors: ResizeCursors = Object.assign({}, DEFAULT_RESIZE_CURSORS, this.resizeCursors);
                if (currentResize) {
                    const cursor: string = this.getResizeCursor(currentResize.edges, resizeCursors);
                    this.renderer.setStyle(document.body, 'cursor', cursor);
                } else {
                    const cursor: string = this.getResizeCursor(resizeEdges, resizeCursors);
                    this.renderer.setStyle(this.containerElement.nativeElement, 'cursor', cursor);
                }
                this.setElementClass(this.containerElement, RESIZE_ACTIVE_CLASS, !!currentResize);
                this.setElementClass(this.containerElement, RESIZE_LEFT_HOVER_CLASS, resizeEdges.left === true);
                this.setElementClass(this.containerElement, RESIZE_RIGHT_HOVER_CLASS, resizeEdges.right === true);
                this.setElementClass(this.containerElement, RESIZE_TOP_HOVER_CLASS, resizeEdges.top === true);
                this.setElementClass(this.containerElement, RESIZE_BOTTOM_HOVER_CLASS, resizeEdges.bottom === true);
            });

            const mousedrag: Observable<any> = this.mousedown.pipe(
                mergeMap((startCoords) =>
                    merge(
                        mouseMove.pipe(
                            take(1),
                            map((coords) => [undefined, coords])
                        ),
                        mouseMove.pipe(
                            pairwise(),
                            map(([previousCoords, newCoords]) => [
                                previousCoords ? this.getDiff(startCoords, previousCoords) : previousCoords,
                                this.getDiff(startCoords, newCoords),
                            ]),
                            filter(([previousCoords, newCoords]) => {
                                if (!previousCoords) {
                                    return true;
                                }

                                const snapGrid: Coordinate = this.getSnapGrid(currentResize);
                                const previousGrid: Coordinate = this.getGrid(previousCoords, snapGrid);
                                const newGrid: Coordinate = this.getGrid(newCoords, snapGrid);

                                return previousGrid.x !== newGrid.x || previousGrid.y !== newGrid.y;
                            }),
                            map(([, newCoords]) => {
                                const snapGrid: Coordinate = this.getSnapGrid(currentResize);
                                return {
                                    clientX: Math.round(newCoords.clientX / snapGrid.x) * snapGrid.x,
                                    clientY: Math.round(newCoords.clientY / snapGrid.y) * snapGrid.y,
                                };
                            }),
                            takeUntil(merge(this.mouseup, this.mousedown))
                        )
                    )
                ),
                filter(() => !!currentResize)
            );

            mousedrag
                .pipe(
                    map(({ clientX, clientY }) =>
                        this.getNewBoundingRectangle(currentResize.startingRect, currentResize.edges, clientX, clientY)
                    ),
                    filter(
                        (newBoundingRect: BoundingRectangle) =>
                            !!newBoundingRect.height &&
                            !!newBoundingRect.width &&
                            newBoundingRect.height > 0 &&
                            newBoundingRect.width > 0
                    ),
                    filter((newBoundingRect: BoundingRectangle) =>
                        this.validateResize
                            ? this.validateResize({
                                  rectangle: newBoundingRect,
                                  edges: this.getEdgesDiff({
                                      edges: currentResize.edges,
                                      initialRectangle: currentResize.startingRect,
                                      newRectangle: newBoundingRect,
                                  }),
                              })
                            : true
                    )
                )
                .subscribe((newBoundingRect: BoundingRectangle) => {
                    if (currentResize && currentResize.clonedNode) {
                        this.renderer.setStyle(currentResize.clonedNode, 'height', `${newBoundingRect.height}px`);
                        this.renderer.setStyle(currentResize.clonedNode, 'width', `${newBoundingRect.width}px`);
                        this.renderer.setStyle(currentResize.clonedNode, 'top', `${newBoundingRect.top}px`);
                        this.renderer.setStyle(currentResize.clonedNode, 'left', `${newBoundingRect.left}px`);
                    }

                    this.ngZone.run(() => {
                        this.resizing.emit({
                            edges: this.getEdgesDiff({
                                edges: currentResize.edges,
                                initialRectangle: currentResize.startingRect,
                                newRectangle: newBoundingRect,
                            }),
                            rectangle: newBoundingRect,
                        });
                    });

                    currentResize.currentRect = newBoundingRect;
                });

            this.mousedown
                .pipe(
                    map(
                        ({ clientX, clientY, edges }) =>
                            edges ||
                            this.getResizeEdges(
                                clientX,
                                clientY,
                                this.containerElement,
                                this.resizeEdges,
                                this.resizeCursorPrecision
                            )
                    ),
                    filter((edges: Edges) => Object.keys(edges).length > 0)
                )
                .subscribe((edges: Edges) => {
                    if (currentResize) {
                        removeGhostElement();
                    }
                    const startingRect = this.getElementRect(this.containerElement, this.ghostElementPositioning);
                    currentResize = {
                        edges,
                        startingRect,
                        currentRect: startingRect,
                    };
                    if (this.enableGhostResize) {
                        currentResize.clonedNode = this.containerElement.nativeElement.cloneNode(true);
                        const resizeCursors: ResizeCursors = Object.assign(
                            {},
                            DEFAULT_RESIZE_CURSORS,
                            this.resizeCursors
                        );
                        this.containerElement.nativeElement.parentElement.appendChild(currentResize.clonedNode);
                        this.renderer.addClass(this.containerElement.nativeElement, 'resizing-hidden');
                        this.renderer.setStyle(currentResize.clonedNode, 'position', this.ghostElementPositioning);
                        this.renderer.setStyle(
                            currentResize.clonedNode,
                            'left',
                            `${currentResize.startingRect.left}px`
                        );
                        this.renderer.setStyle(currentResize.clonedNode, 'top', `${currentResize.startingRect.top}px`);
                        this.renderer.setStyle(
                            currentResize.clonedNode,
                            'height',
                            `${currentResize.startingRect.height}px`
                        );
                        this.renderer.setStyle(
                            currentResize.clonedNode,
                            'width',
                            `${currentResize.startingRect.width}px`
                        );
                        this.renderer.setStyle(
                            currentResize.clonedNode,
                            'cursor',
                            this.getResizeCursor(currentResize.edges, resizeCursors)
                        );
                        this.renderer.addClass(currentResize.clonedNode, RESIZE_GHOST_ELEMENT_CLASS);
                        currentResize.clonedNode.scrollTop = currentResize.startingRect.scrollTop as number;
                        currentResize.clonedNode.scrollLeft = currentResize.startingRect.scrollLeft as number;
                    }
                    this.ngZone.run(() => {
                        this.resizeStart.emit({
                            edges: this.getEdgesDiff({
                                edges,
                                initialRectangle: startingRect,
                                newRectangle: startingRect,
                            }),
                            rectangle: this.getNewBoundingRectangle(startingRect, {}, 0, 0),
                        });
                    });
                });

            this.mouseup.subscribe(() => {
                if (currentResize) {
                    this.renderer.removeClass(this.containerElement.nativeElement, RESIZE_ACTIVE_CLASS);
                    this.renderer.setStyle(document.body, 'cursor', '');
                    this.renderer.setStyle(this.containerElement.nativeElement, 'cursor', '');
                    this.ngZone.run(() => {
                        this.resizeEnd.emit({
                            edges: this.getEdgesDiff({
                                edges: currentResize.edges,
                                initialRectangle: currentResize.startingRect,
                                newRectangle: currentResize.currentRect,
                            }),
                            rectangle: currentResize.currentRect,
                        });
                    });
                    removeGhostElement();
                    currentResize = null;
                }
            });
        }
    }

    /**
     * @hidden
     */
    ngOnDestroy(): void {
        if (this.mousedown) {
            this.mousedown.complete();
        }
        if (this.mouseup) {
            this.mouseup.complete();
        }
        if (this.mousemove) {
            this.mousemove.complete();
        }
        if (this.listenerSubscriptions) {
            if (this.listenerSubscriptions.pointerDown$) {
                this.listenerSubscriptions.pointerDown$.unsubscribe();
            }
            if (this.listenerSubscriptions.pointerMove$) {
                this.listenerSubscriptions.pointerMove$.unsubscribe();
            }
            if (this.listenerSubscriptions.pointerUp$) {
                this.listenerSubscriptions.pointerUp$.unsubscribe();
            }
        }
    }

    ngDoCheck() {
        // this.logger.warn('ngDoCheck');
    }

    private setElementClass(elm: ElementRef, name: string, add: boolean): void {
        if (add) {
            this.renderer.addClass(elm.nativeElement, name);
        } else {
            this.renderer.removeClass(elm.nativeElement, name);
        }
    }

    private getGrid(coords: { clientX: number; clientY: number }, snapGrid: Coordinate) {
        return {
            x: Math.ceil(coords.clientX / snapGrid.x),
            y: Math.ceil(coords.clientY / snapGrid.y),
        };
    }

    private getSnapGrid(currentResize: ResizeObject) {
        const snapGrid: Coordinate = { x: 1, y: 1 };

        if (currentResize) {
            if (this.resizeSnapGrid.left && currentResize.edges.left) {
                snapGrid.x = +this.resizeSnapGrid.left;
            } else if (this.resizeSnapGrid.right && currentResize.edges.right) {
                snapGrid.x = +this.resizeSnapGrid.right;
            }

            if (this.resizeSnapGrid.top && currentResize.edges.top) {
                snapGrid.y = +this.resizeSnapGrid.top;
            } else if (this.resizeSnapGrid.bottom && currentResize.edges.bottom) {
                snapGrid.y = +this.resizeSnapGrid.bottom;
            }
        }

        return snapGrid;
    }

    private isNumberCloseTo(value1: number, value2: number, precision = 3): boolean {
        const diff: number = Math.abs(value1 - value2);
        return diff < precision;
    }

    private getNewBoundingRectangle(
        startingRect: BoundingRectangle,
        edges: Edges,
        clientX: number,
        clientY: number
    ): BoundingRectangle {
        const newBoundingRect: BoundingRectangle = {
            top: startingRect.top,
            bottom: startingRect.bottom,
            left: startingRect.left,
            right: startingRect.right,
        };

        if (edges.top) {
            newBoundingRect.top += clientY;
        }
        if (edges.bottom) {
            newBoundingRect.bottom += clientY;
        }
        if (edges.left) {
            newBoundingRect.left += clientX;
        }
        if (edges.right) {
            newBoundingRect.right += clientX;
        }
        newBoundingRect.height = newBoundingRect.bottom - newBoundingRect.top;
        newBoundingRect.width = newBoundingRect.right - newBoundingRect.left;

        return newBoundingRect;
    }

    private getElementRect(element: ElementRef, ghostElementPositioning: string): BoundingRectangle {
        let translateX = 0;
        let translateY = 0;
        const style = element.nativeElement.style;
        const transformProperties = ['transform', '-ms-transform', '-moz-transform', '-o-transform'];
        const transform = transformProperties.map((property) => style[property]).find((value) => !!value);
        if (transform && transform.includes('translate')) {
            const matcher = (_m: any, p1: string, p2: string) => {
                translateX = parseFloat(p1) || 0;
                translateY = parseFloat(p2) || 0;
            };
            transform.replace(/.*translate\(([\-+]?\d+)(?:px)?(?:\s*,\s*([\-+]?\d+)(?:px)?)?\).*/, matcher);
        }

        if (ghostElementPositioning === 'absolute') {
            return {
                height: element.nativeElement.offsetHeight,
                width: element.nativeElement.offsetWidth,
                top: element.nativeElement.offsetTop,
                bottom: element.nativeElement.offsetHeight + element.nativeElement.offsetTop,
                left: element.nativeElement.offsetLeft,
                right: element.nativeElement.offsetWidth + element.nativeElement.offsetLeft,
            };
        } else {
            const boundingRect: BoundingRectangle = element.nativeElement.getBoundingClientRect();
            return {
                height: boundingRect.height,
                width: boundingRect.width,
                top: boundingRect.top - translateY,
                bottom: boundingRect.bottom + translateY,
                left: boundingRect.left - translateX,
                right: boundingRect.right + translateX,
                scrollTop: element.nativeElement.scrollTop,
                scrollLeft: element.nativeElement.scrollLeft,
            };
        }
    }

    private isWithinBoundingX({ clientX, rect }: { clientX: number; rect: DOMRect }): boolean {
        return clientX >= rect.left && clientX <= rect.right;
    }

    private isWithinBoundingY({ clientY, rect }: { clientY: number; rect: DOMRect }): boolean {
        return clientY >= rect.top && clientY <= rect.bottom;
    }

    private getResizeEdges(
        clientX: number,
        clientY: number,
        elm: ElementRef,
        allowedEdges: Edges,
        cursorPrecision: number
    ): Edges {
        // TODO: Read element position - isolate with requestAnimationFrame
        const elmPosition: DOMRect = elm.nativeElement.getBoundingClientRect();
        const edges: Edges = {};

        if (
            allowedEdges.left &&
            this.isNumberCloseTo(clientX, elmPosition.left, cursorPrecision) &&
            this.isWithinBoundingY({ clientY, rect: elmPosition })
        ) {
            edges.left = true;
        }

        if (
            allowedEdges.right &&
            this.isNumberCloseTo(clientX, elmPosition.right, cursorPrecision) &&
            this.isWithinBoundingY({ clientY, rect: elmPosition })
        ) {
            edges.right = true;
        }

        if (
            allowedEdges.top &&
            this.isNumberCloseTo(clientY, elmPosition.top, cursorPrecision) &&
            this.isWithinBoundingX({ clientX, rect: elmPosition })
        ) {
            edges.top = true;
        }

        // this.logger.warn(`clientY: ${clientY}, bottomEdge: ${elmPosition.bottom}`);
        if (
            allowedEdges.bottom &&
            this.isNumberCloseTo(clientY, elmPosition.bottom, cursorPrecision) &&
            this.isWithinBoundingX({ clientX, rect: elmPosition })
        ) {
            edges.bottom = true;
        }

        return edges;
    }

    private getResizeCursor(edges: Edges, cursors: ResizeCursors): string {
        if (edges.left && edges.top) {
            return cursors.topLeft;
        } else if (edges.right && edges.top) {
            return cursors.topRight;
        } else if (edges.left && edges.bottom) {
            return cursors.bottomLeft;
        } else if (edges.right && edges.bottom) {
            return cursors.bottomRight;
        } else if (edges.left || edges.right) {
            return cursors.leftOrRight;
        } else if (edges.top || edges.bottom) {
            return cursors.topOrBottom;
        } else {
            return '';
        }
    }

    private getDiff(startCoords: ClientCoordinate, moveCoords: ClientCoordinate) {
        return {
            clientX: moveCoords.clientX - startCoords.clientX,
            clientY: moveCoords.clientY - startCoords.clientY,
        };
    }

    private getEdgesDiff({
        edges,
        initialRectangle,
        newRectangle,
    }: {
        edges: Edges;
        initialRectangle: BoundingRectangle;
        newRectangle: BoundingRectangle;
    }): Edges {
        const edgesDiff: Edges = {};
        Object.keys(edges).forEach((edge: keyof Edges) => {
            edgesDiff[edge] = newRectangle[edge] - initialRectangle[edge];
        });
        return edgesDiff;
    }
}
